// src/components/RightSidebar.jsx (DROP-IN)
// Adds a "Highlighted Trade" hero rotator above Ads Space.
// - Loads highlighted trades from PublicTrades (safe for guests)
// - Auto-rotates; pauses on hover/focus; manual next/prev; click navigates to /trade/:id
// - Keeps your existing Ads behavior intact

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { db } from "../firebase";
import { collection, getDocs, limit, orderBy, query } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import "./RightSidebar.css";

const toMillisSafe = (ts) => {
  try {
    if (!ts) return 0;
    if (typeof ts.toMillis === "function") return ts.toMillis();
    if (typeof ts.toDate === "function") return ts.toDate().getTime();
    if (ts instanceof Date) return ts.getTime();
    if (typeof ts === "number") return ts;
    if (typeof ts.seconds === "number") return ts.seconds * 1000;
    return 0;
  } catch {
    return 0;
  }
};

const formatDate = (ts) => {
  const ms = toMillisSafe(ts);
  if (!ms) return "";
  return new Date(ms).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

const moneyZAR = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return "";
  return num.toLocaleString("en-ZA", { style: "currency", currency: "ZAR" });
};

const fallbackSvgDataUri = (title = "Highlighted Trade") => {
  const safe = String(title).slice(0, 28);
  const svg = `
  <svg xmlns="http://www.w3.org/2000/svg" width="600" height="400">
    <defs>
      <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stop-color="#fff7ed"/>
        <stop offset="1" stop-color="#f8fafc"/>
      </linearGradient>
    </defs>
    <rect width="100%" height="100%" fill="url(#g)"/>
    <circle cx="92" cy="112" r="44" fill="#fde68a"/>
    <rect x="160" y="92" width="360" height="34" rx="10" fill="#e5e7eb"/>
    <rect x="160" y="142" width="280" height="26" rx="10" fill="#eef2f7"/>
    <text x="50%" y="78%" font-family="Arial, sans-serif" font-size="26" text-anchor="middle" fill="#0f172a" font-weight="700">
      ${safe.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}
    </text>
    <text x="50%" y="86%" font-family="Arial, sans-serif" font-size="14" text-anchor="middle" fill="#64748b">
      No image provided
    </text>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const getPrimaryImage = (trade) => {
  const candidates =
    trade?.imageURLs ??
    trade?.imageUrls ??
    trade?.images ??
    trade?.imageURL ??
    trade?.imageUrl ??
    null;

  if (Array.isArray(candidates) && candidates.length) return candidates[0];
  if (typeof candidates === "string" && candidates.trim()) return candidates.trim();
  return null;
};

const toMs = (until) =>
  until?.toMillis?.() ?? (typeof until?.seconds === "number" ? until.seconds * 1000 : 0);

const isActive = (until) => {
  const ms = toMs(until);
  return !!ms && ms > Date.now();
};

const isHighlighted = (t) => {
  const p = t?.promotions || null;
  if (!p) return false;

  // New model (preferred)
  if (isActive(p.highlightUntil)) return true;

  // Legacy fallback
  if (p.highlight === true) return true;

  return false;
};

const normalizeForUI = (t) => {
  const cash =
    t?.expectedCashAmount ??
    t?.cashExpected ??
    t?.expectedCash ??
    t?.estimatedValue ??
    t?.price ??
    null;

  return {
    ...t,
    expectedCashAmount: cash,
    title: t?.title ?? t?.itemName ?? "Untitled",
    condition: t?.condition ?? "Used",
  };
};

export default function RightSidebar() {
  const navigate = useNavigate();

  // ---------- Highlighted trades (new) ----------
  const [highlighted, setHighlighted] = useState([]);
  const [hlLoading, setHlLoading] = useState(true);
  const [hlErr, setHlErr] = useState("");
  const [hlIndex, setHlIndex] = useState(0);
  const [hlPaused, setHlPaused] = useState(false);

  // ✅ FIX: pauseBriefly must live inside the component (hook + state access)
  const pauseTimerRef = useRef(null);
  const pauseBriefly = useCallback(
    (ms = 6000) => {
      setHlPaused(true);
      window.clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = window.setTimeout(() => setHlPaused(false), ms);
    },
    []
  );

  // ---------- Ads (existing) ----------
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [scrollAdPage, setScrollAdPage] = useState(0);
  const scrollContainerRef = useRef(null);

  // cleanup pause timer on unmount
  useEffect(() => {
    return () => window.clearTimeout(pauseTimerRef.current);
  }, []);

  // Fetch highlighted trades from PublicTrades (safe)
  useEffect(() => {
    let alive = true;

    (async () => {
      setHlLoading(true);
      setHlErr("");

      try {
        const qRef = query(
          collection(db, "PublicTrades"),
          orderBy("createdAt", "desc"),
          limit(80)
        );

        const snap = await getDocs(qRef);
        if (!alive) return;

        const list = snap.docs.map((d) => normalizeForUI({ id: d.id, ...d.data() }));
        const hi = list.filter(isHighlighted);

        setHighlighted(hi.slice(0, 10));
        setHlIndex(0);
      } catch (e) {
        console.error("Highlighted trades fetch failed:", e);
        if (!alive) return;
        setHlErr("Unable to load highlighted trades right now.");
        setHighlighted([]);
      } finally {
        if (!alive) return;
        setHlLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  // Auto-rotate highlighted hero (pause on hover/focus)
  useEffect(() => {
    if (highlighted.length <= 1) return;
    if (hlPaused) return;

    const t = setInterval(() => {
      setHlIndex((i) => (i + 1) % highlighted.length);
    }, 5200);

    return () => clearInterval(t);
  }, [highlighted.length, hlPaused]);

  const goNextHighlight = useCallback(() => {
    pauseBriefly();
    setHlIndex((i) => (i + 1) % (highlighted.length || 1));
  }, [highlighted.length, pauseBriefly]);

  const goPrevHighlight = useCallback(() => {
    pauseBriefly();
    setHlIndex((i) => (i - 1 + (highlighted.length || 1)) % (highlighted.length || 1));
  }, [highlighted.length, pauseBriefly]);

  const activeHighlight = useMemo(() => {
    if (!highlighted.length) return null;
    return highlighted[Math.min(hlIndex, highlighted.length - 1)] || null;
  }, [highlighted, hlIndex]);

  // ---------- Ads: fetch (existing) ----------
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "Adverts"));
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setAds(list.slice(0, 10));
      } catch (e) {
        console.error("Advert fetch failed:", e);
        setErr("Unable to load ads right now.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Ads rotate top banner
  useEffect(() => {
    if (ads.length <= 1) return;
    const t = setInterval(() => {
      setCurrentAdIndex((i) => (i + 1) % ads.length);
    }, 6000);
    return () => clearInterval(t);
  }, [ads.length]);

  // Ads scroll paging for bottom list
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const onScroll = () => {
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
        const pages = Math.max(1, Math.ceil(Math.max(0, ads.length - 1) / 2));
        setScrollAdPage((p) => (p + 1) % pages);
      }
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [ads.length]);

  const paginatedAds = ads.slice(1).slice(scrollAdPage * 2, scrollAdPage * 2 + 2);

  const onHighlightClick = () => {
    if (!activeHighlight?.id) return;
    navigate(`/trade/${activeHighlight.id}`);
  };

  const highlightImg = (() => {
    const p = getPrimaryImage(activeHighlight);
    const ok = typeof p === "string" && /^https?:\/\//.test(p);
    return ok ? p : fallbackSvgDataUri(activeHighlight?.title || "Highlighted Trade");
  })();

  const hlCash = activeHighlight?.expectedCashAmount;
  const hlCashText = moneyZAR(hlCash);
  const hlDate = formatDate(activeHighlight?.createdAt);

  return (
    <aside className="right-sidebar">
      {/* =========================
          0) Highlighted Trade Hero
          ========================= */}
      <div className="sidebar-highlight">
        <div className="sidebar-highlight__header">
          <div className="sidebar-highlight__titleWrap">
            <h3 className="sidebar-highlight__title">Highlighted Trade</h3>
            <span className="sidebar-highlight__pill">Premium</span>
          </div>

          {highlighted.length > 1 && (
            <div className="sidebar-highlight__nav">
              <button
                type="button"
                className="sidebar-highlight__navBtn"
                onClick={goPrevHighlight}
                aria-label="Previous highlighted trade"
                onMouseEnter={() => setHlPaused(true)}
                onMouseLeave={() => setHlPaused(false)}
              >
                ‹
              </button>
              <button
                type="button"
                className="sidebar-highlight__navBtn"
                onClick={goNextHighlight}
                aria-label="Next highlighted trade"
                onMouseEnter={() => setHlPaused(true)}
                onMouseLeave={() => setHlPaused(false)}
              >
                ›
              </button>
            </div>
          )}
        </div>

        {hlLoading ? (
          <div className="highlight-card" style={{ padding: 14 }}>
            Loading highlighted trade…
          </div>
        ) : hlErr ? (
          <div className="highlight-card" style={{ padding: 14, color: "#b00020" }}>
            {hlErr}
          </div>
        ) : !activeHighlight ? (
          <div className="highlight-card placeholder">No highlighted trades yet.</div>
        ) : (
          <div
            className="highlight-heroWrap"
            onMouseEnter={() => setHlPaused(true)}
            onMouseLeave={() => setHlPaused(false)}
            onFocusCapture={() => setHlPaused(true)}
            onBlurCapture={() => setHlPaused(false)}
          >
            <AnimatePresence mode="wait">
              <motion.button
                key={activeHighlight.id}
                type="button"
                className="highlight-card highlight-card--hero"
                onClick={onHighlightClick}
                initial={{ opacity: 0, y: 10, scale: 0.995 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.995 }}
                transition={{ duration: 0.35 }}
              >
                <div className="highlight-imgWrap">
                  <img
                    src={highlightImg}
                    alt={activeHighlight?.title || "Highlighted trade"}
                    className="highlight-img"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = fallbackSvgDataUri(
                        activeHighlight?.title || "Highlighted Trade"
                      );
                    }}
                  />

                  <div className="highlight-shade" aria-hidden="true" />
                  <span className="highlight-badge">⭐ Highlighted</span>

                  {hlCashText ? (
                    <span className="highlight-price" title="Cash amount (if applicable)">
                      {hlCashText}
                    </span>
                  ) : null}

                  <span
                    className={`highlight-condition ${
                      String(activeHighlight?.condition || "").toLowerCase() === "new"
                        ? "isNew"
                        : "isUsed"
                    }`}
                  >
                    {String(activeHighlight?.condition || "").toLowerCase() === "new"
                      ? "🆕 New"
                      : "♻️ Used"}
                  </span>

                  {hlDate ? <span className="highlight-date">📅 {hlDate}</span> : null}

                  <div className="highlight-cta" aria-hidden="true">
                    View trade →
                  </div>
                </div>

                <div className="highlight-body">
                  <div className="highlight-title" title={activeHighlight?.title || ""}>
                    {activeHighlight?.title || "Untitled"}
                  </div>

                  <div className="highlight-sub">
                    {String(activeHighlight?.description || "Tap to view details.").slice(0, 72)}
                    {String(activeHighlight?.description || "").length > 72 ? "…" : ""}
                  </div>
                </div>
              </motion.button>
            </AnimatePresence>

            {highlighted.length > 1 && (
              <div className="highlight-dots" aria-label="Highlighted trade position">
                {highlighted.map((t, idx) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`highlight-dot ${idx === hlIndex ? "isActive" : ""}`}
                    onClick={() => {
                      pauseBriefly();
                      setHlIndex(idx);
                    }}
                    aria-label={`Show highlighted trade ${idx + 1}`}
                    onMouseEnter={() => setHlPaused(true)}
                    onMouseLeave={() => setHlPaused(false)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* =========================
          1) Ads Space (existing)
          ========================= */}
      <h3 className="sidebar-title">Ads Space</h3>

      {loading && (
        <div className="ad-card" style={{ padding: 16 }}>
          Loading ads…
        </div>
      )}

      {!loading && err && (
        <div className="ad-card" style={{ padding: 16, color: "#b00020" }}>
          {err}
        </div>
      )}

      {!loading && !err && ads.length === 0 && (
        <div className="ad-card placeholder">No ads yet. Check back soon.</div>
      )}

      {!loading && !err && ads.length > 0 && (
        <>
          <div className="ad-section">
            <AnimatePresence mode="wait">
              <motion.div
                key={ads[currentAdIndex]?.id || "fallback"}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4 }}
                className="ad-card fade-banner"
              >
                <a href={ads[currentAdIndex]?.link || "#"} target="_blank" rel="noopener noreferrer">
                  <img
                    src={ads[currentAdIndex]?.imageURL || "/placeholder-ad.jpg"}
                    alt={ads[currentAdIndex]?.title || "Sponsored"}
                    className="ad-image"
                  />
                  <div className="ad-content">
                    <h4>{ads[currentAdIndex]?.title || "Sponsored"}</h4>
                    <p>{(ads[currentAdIndex]?.description || "Click to learn more.").slice(0, 70)}…</p>
                  </div>
                </a>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="ad-scroll-section" ref={scrollContainerRef}>
            {paginatedAds.map((ad) => (
              <div key={ad.id} className="ad-card">
                <a href={ad.link || "#"} target="_blank" rel="noopener noreferrer">
                  <img
                    src={ad.imageURL || "/placeholder-ad.jpg"}
                    alt={ad.title || "Sponsored"}
                    className="ad-image"
                  />
                  <div className="ad-content">
                    <h4>{ad.title || "Sponsored"}</h4>
                    <p>{(ad.description || "Click to learn more.").slice(0, 50)}…</p>
                  </div>
                </a>
              </div>
            ))}
          </div>
        </>
      )}
    </aside>
  );
}
